import { PlaycastWebrtcPreset, PlaycastWebrtcPresetGroup, getReceiverCapabilities, RTCRtpCodecCapability } from '@playcastdotio/Peer';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@playcastdotio/Shadcn';
import { useContext, useEffect, useState } from 'react';
import { PlaycastContext } from '../PlaycastWrapper';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye } from '@fortawesome/pro-solid-svg-icons';

export const PresetControls = () => {
  const context = useContext(PlaycastContext);
  const [presets, setPresets] = useState<PlaycastWebrtcPresetGroup>();
  const [selectedPresetKey, setSelectedPresetKey] = useState<string>();
  const [currentPreset, setCurrentPreset] = useState<PlaycastWebrtcPreset>();
  const [localPreset, setLocalPreset] = useState<PlaycastWebrtcPreset>();
  const [handlePanic, setHandlePanic] = useState<boolean>(true);
  const [capabilities, setCapabilities] = useState<RTCRtpCodecCapability[]>();
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    setPresets(context.stateStores.webrtcPresetGroups);
    if (!selectedPresetKey && presets) {
      setSelectedPresetKey(Object.keys(presets)[0]);
    }
  }, [context.stateStores.webrtcPresetGroups]);

  useEffect(() => {
    setPresets(context.stateStores.webrtcPresetGroups);
    if (presets && selectedPresetKey) {
      setCurrentPreset(presets[selectedPresetKey]);
      setLocalPreset(presets[selectedPresetKey]);
    }
    try {
      const cap = getReceiverCapabilities();
      setCapabilities(cap);
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    if (presets && selectedPresetKey) {
      setCurrentPreset(presets[selectedPresetKey]);
      setLocalPreset(presets[selectedPresetKey]);
    }
  }, [presets, selectedPresetKey]);

  const handlePresetChanged = (key: string) => {
    setSelectedPresetKey(key);
    if (presets) {
      setLocalPreset(presets[key]);
      setHasChanges(false);
    }
  };

  const handlePanicChange = (value: boolean) => {
    setHandlePanic(value);
    setHasChanges(true);
  };

  const handleVideoSendChange = (key: any, value: any) => {
    if (!localPreset || !selectedPresetKey) return;

    setLocalPreset({
      ...localPreset,
      videoSend: {
        ...localPreset.videoSend,
        [key]: value,
      },
    });
    setHasChanges(true);
  };

  const handleResolutionChange = (dimension: 'width' | 'height', value: number) => {
    if (!localPreset || !selectedPresetKey) return;

    setLocalPreset({
      ...localPreset,
      videoSend: {
        ...localPreset.videoSend,
      },
    });
    setHasChanges(true);
  };

  const handleVideoReceiveChange = (key: keyof PlaycastWebrtcPreset['videoReceive'], value: any) => {
    if (!localPreset || !selectedPresetKey) return;

    setLocalPreset({
      ...localPreset,
      videoReceive: {
        ...localPreset.videoReceive,
        [key]: value,
      },
    });
    setHasChanges(true);
  };

  const handleAudioChange = (key: keyof PlaycastWebrtcPreset['audio'], value: boolean) => {
    if (!localPreset || !selectedPresetKey) return;

    setLocalPreset({
      ...localPreset,
      audio: {
        ...localPreset.audio,
        [key]: value,
      },
    });
    setHasChanges(true);
  };

  const handleFeedbackTypeChange = (
    key: keyof PlaycastWebrtcPresetGroup['default']['videoSend']['disableFeedbackTypes'],
    value: boolean,
  ) => {
    if (!localPreset || !selectedPresetKey) return;
    const updatedPreset = {
      ...localPreset,
      videoSend: {
        ...localPreset.videoSend,
      },
    };
    setLocalPreset(updatedPreset);
    setHasChanges(true);
  };

  const handleEncoderProfileChange = (profile: RTCRtpCodecCapability) => {
    if (!localPreset || !selectedPresetKey) return;

    setLocalPreset({
      ...localPreset,
      videoReceive: {
        ...localPreset.videoReceive,
        encoderProfileOverride: profile,
      },
    });
    setHasChanges(true);
  };

  const handleSave = () => {
    if (!localPreset || !selectedPresetKey || !presets) return;

    const updatedPresets = { ...presets };
    updatedPresets[selectedPresetKey] = localPreset;
    context.targetFunctions.setWebrtcPresetGroup(updatedPresets);
    setHasChanges(false);
  };

  const getCurrentEncoderCapabilities = () => {
    // console.warn('capabilities', capabilities);
    const currentEncoder = currentPreset?.videoReceive?.encoder;
    if (!currentEncoder || !capabilities) return [];

    const encoderName = Array.isArray(currentEncoder) ? currentEncoder[0] : currentEncoder;
    console.log('encoderName', encoderName);
    const filtered = capabilities.filter((cap) => {
      if (cap.mimeType.toLowerCase().includes(encoderName.toLowerCase())) {
        return true;
      }
      return false;
    });
    console.warn('filtered', filtered);
    return filtered;
  };

  const [showContent, setShowContent] = useState(false);

  const handleShowContent = () => {
    if (presets) {
      setShowContent(!showContent);
    }
  };

  return (
    <Card className='mx-4 w-full max-w-2xl rounded-none border-none bg-gray-800 text-white'>
      <CardHeader>
        <CardTitle>
          <div className='flex items-center justify-between'>
            <span>WebRTC Settings</span>
            <div className='flex items-center gap-2'>
              {hasChanges && (
                <Button variant='default' className='bg-green-600 hover:bg-green-700' onClick={handleSave}>
                  Save Changes
                </Button>
              )}
              <Button
                variant='ghost'
                className='text-primary hover:bg-inherit hover:text-yellow-500 disabled:text-gray-400'
                onClick={handleShowContent}
              >
                <FontAwesomeIcon icon={faEye} />
              </Button>
            </div>
          </div>
        </CardTitle>
      </CardHeader>
      {showContent && (
        <CardContent>
          <div className='mb-2 space-y-2'>
            <Select value={selectedPresetKey} onValueChange={handlePresetChanged}>
              <SelectTrigger>
                <SelectValue placeholder='Select a preset' />
              </SelectTrigger>
              <SelectContent>
                {presets &&
                  Object.keys(presets).map((key) => (
                    <SelectItem key={key} value={key}>
                      {key}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>

          {localPreset && (
            <Tabs defaultValue='video-send'>
              <TabsList className='grid w-full grid-cols-3'>
                {localPreset.videoSend && (
                  <TabsTrigger value='video-send'>
                    <span className='text-gray-200'>Video Send</span>
                  </TabsTrigger>
                )}
                {localPreset.videoReceive && (
                  <TabsTrigger value='video-receive'>
                    <span className='text-gray-200'>Video Receive</span>
                  </TabsTrigger>
                )}
                {localPreset.audio && (
                  <TabsTrigger value='audio'>
                    <span className='text-gray-200'>Audio</span>
                  </TabsTrigger>
                )}
              </TabsList>

              {localPreset.videoSend && (
                <TabsContent value='video-send' className='space-y-4'>
                  <div className='grid grid-cols-2 gap-4'>
                    <div className='space-y-2'>
                      <Label htmlFor='framerate'>Framerate</Label>
                      <Input
                        id='framerate'
                        type='number'
                        value={localPreset.videoSend.framerate}
                        onChange={(e) => handleVideoSendChange('framerate', parseInt(e.target.value))}
                      />
                    </div>
                    <div className='space-y-2'>
                      <Label htmlFor='bitrate'>Bitrate</Label>
                      <Input
                        id='bitrate'
                        type='number'
                        value={localPreset.videoSend.bitrate}
                        onChange={(e) => handleVideoSendChange('bitrate', parseInt(e.target.value))}
                      />
                    </div>
                    {/* <div className='space-y-2'>
                      <Label htmlFor='keyframeInterval'>Keyframe Interval</Label>
                      <Input
                        id='keyframeInterval'
                        type='number'
                        value={localPreset.videoSend.keyframeInterval}
                        onChange={(e) => handleVideoSendChange('keyframeInterval', parseInt(e.target.value))}
                      />
                    </div> */}
                    {selectedPresetKey === 'default' && (
                      <div className='space-y-2'>
                        <Label htmlFor='maintainResolutionHoldDelay'>Maintain Resolution Hold Delay (ms)</Label>
                        <Input
                          id='maintainResolutionHoldDelay'
                          type='number'
                          value={localPreset.videoSend.maintainResolutionHoldDelay}
                          onChange={(e) => handleVideoSendChange('maintainResolutionHoldDelay', parseInt(e.target.value))}
                        />
                      </div>
                    )}
                    <div className='space-y-2'>
                      <Label htmlFor='degradationPreference'>Degradation Preference</Label>
                      <Select
                        value={localPreset.videoSend.degradationPreference}
                        onValueChange={(value) => handleVideoSendChange('degradationPreference', value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder='Select preference' />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value='maintain-framerate'>Maintain Framerate</SelectItem>
                          <SelectItem value='maintain-resolution'>Maintain Resolution</SelectItem>
                          <SelectItem value='balanced'>Balanced</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {selectedPresetKey === 'default' && (
                      <div className='space-y-2'>
                        {/* <Label htmlFor='handlePanicDropdown'>Handle Panic</Label>
                        <Select value={handlePanic ? 'yes' : 'no'} onValueChange={(value) => handlePanicChange(value === 'yes')}>
                          <SelectTrigger>
                            <SelectValue placeholder='Select preference' />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value='yes'>Yes</SelectItem>
                            <SelectItem value='no'>No</SelectItem>
                          </SelectContent>
                        </Select> */}
                      </div>
                    )}
                  </div>
                </TabsContent>
              )}

              {localPreset.videoReceive && (
                <TabsContent value='video-receive' className='space-y-4'>
                  <div className='space-y-2'>
                    <Label htmlFor='encoderProfile'>Encoder Profile</Label>
                    <Select
                      value={JSON.stringify(localPreset.videoReceive?.encoderProfileOverride)}
                      onValueChange={(value) => handleEncoderProfileChange(JSON.parse(value))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder='Select encoder profile' />
                      </SelectTrigger>
                      <SelectContent>
                        {getCurrentEncoderCapabilities().map((profile, index) => (
                          <SelectItem key={index} value={JSON.stringify(profile)}>
                            {profile.mimeType} - {profile.clockRate}Hz
                            {profile.sdpFmtpLine && ` (${profile.sdpFmtpLine})`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </TabsContent>
              )}

              {localPreset.audio && (
                <TabsContent value='audio' className='space-y-4'>
                  <div className='space-y-4'>
                    <div className='flex items-center justify-between'>
                      <Label htmlFor='echoCancellation'>Echo Cancellation</Label>
                      <Switch
                        id='echoCancellation'
                        checked={localPreset?.audio.echoCancellation}
                        onCheckedChange={(checked) => handleAudioChange('echoCancellation', checked)}
                      />
                    </div>
                    <div className='flex items-center justify-between'>
                      <Label htmlFor='noiseSuppression'>Noise Suppression</Label>
                      <Switch
                        id='noiseSuppression'
                        checked={localPreset?.audio.noiseSuppression}
                        onCheckedChange={(checked) => handleAudioChange('noiseSuppression', checked)}
                      />
                    </div>
                    <div className='flex items-center justify-between'>
                      <Label htmlFor='autoGainControl'>Auto Gain Control</Label>
                      <Switch
                        id='autoGainControl'
                        checked={localPreset?.audio.autoGainControl}
                        onCheckedChange={(checked) => handleAudioChange('autoGainControl', checked)}
                      />
                    </div>
                  </div>
                </TabsContent>
              )}
            </Tabs>
          )}
        </CardContent>
      )}
    </Card>
  );
};
